import FactSheetConverter from '@/converters/2.6/FactSheetConverter';
import WorkFileConverter from '@/converters/2.6/WorkFileConverter';
import IComp from '@/models/Comp';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import FactSheetPropertyUtils from '@/models/fact-sheet/FactSheetPropertyUtils';
import IFactSheet from '@/models/fact-sheet/IFactSheet';
import IFactSheetProperty from '@/models/fact-sheet/IFactSheetProperty';
import IWorkFile from '@/models/fact-sheet/IWorkFile';
import { mlsStore } from '@/store';
import ProductFeaturesUtils from '@/utils/ProductFeaturesUtils';
import utils from '@/utils/QSUtils';
import SalesHistoryManager from './SalesHistoryManager';

export default {
    /**
     * Create a fact sheet from the given comp, including the sales history.
     * Sales history requires Smart Data be enabled (Product Features) for the user.
     * @param comp
     * @returns
     */
    async createFromComp(comp: IComp): Promise<IFactSheet> {
        const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(comp.address);
        const mlsPropertyItem = mlsStore.MlsPropertyByAddress1UnitNoZipCode(address1UnitNoZipCode);

        const factSheet = FactSheetConverter.convert(comp, mlsPropertyItem);

        const smartDataEnabled = ProductFeaturesUtils.isSmartDataEnabled();
        if (smartDataEnabled) {
            // Get any sales history and add it to the fact sheet property
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
            if (utils.isKeyInObject(factSheet, address1AndUnitNo)) {
                const factSheetProperty = factSheet[address1AndUnitNo];
                await addSalesHistory(factSheetProperty, comp.address);
            }
        }

        return factSheet;
    },

    /**
     * Create a work file populated with fact sheet properties, including sales histories, from
     * the given comp final data.
     * Sales histories requires Smart Data be enabled (Product Features) for the user.
     *
     * @param compFinalData
     * @returns
     */
    async createFromCompFinalData(compFinalData: ICompFinalData): Promise<IWorkFile> {
        const mlsPropertyData = mlsStore.MlsProperties;

        const workFile = WorkFileConverter.convert(compFinalData, mlsPropertyData);

        const smartDataEnabled = ProductFeaturesUtils.isSmartDataEnabled();
        if (smartDataEnabled) {
            // Get any sales history and add it to the fact sheet properties
            for (const factSheetProperty of workFile) {
                const compAddress = FactSheetPropertyUtils.buildCompAddress(factSheetProperty);
                await addSalesHistory(factSheetProperty, compAddress);
            }
        }

        return workFile;
    },
};

async function addSalesHistory(factSheetProperty: IFactSheetProperty, compAddress: ICompAddress): Promise<boolean> {
    const salesHistoryProperty = await SalesHistoryManager.getByCompAddress(compAddress);
    if (salesHistoryProperty.salesHistory.length > 0) {
        factSheetProperty.salesHistory = salesHistoryProperty.salesHistory;
        return true;
    }
    return false;
}
